/**
 * Push notifications — native-only wiring for @capacitor/push-notifications.
 *
 * WHEN THIS RUNS: `setupPushNotifications()` is called from AuthContext
 * once a user is signed in AND has at least one connected league — never
 * from the welcome or login screen. Apple's own guidance (and just good UX)
 * is to never front-load a permission prompt before the user has a reason
 * to want it; "get reminded when your draft starts" only makes sense once
 * there's a league with a draft to be reminded about.
 *
 * `teardownPushNotifications()` runs on logout, deleting this device's
 * token server-side (routes/devices.js) so a signed-out device stops
 * receiving another account's draft reminders.
 *
 * Everything here is a no-op on web — every exported function checks
 * IS_NATIVE_APP itself, so call sites don't need their own guard.
 */

import { PushNotifications } from '@capacitor/push-notifications'
import { IS_NATIVE_APP } from './runtime'
import { devicesApi } from './api'

let listenersAttached = false
let lastRegisteredToken = null

/**
 * Requests permission (if not already granted/denied) and registers this
 * device with APNs, then saves the resulting token server-side. Safe to
 * call more than once — Capacitor's own `register()` is idempotent, and the
 * backend's registerDevice upserts by token.
 */
export async function setupPushNotifications() {
  if (!IS_NATIVE_APP) return

  attachListenersOnce()

  try {
    const permStatus = await PushNotifications.checkPermissions()
    let receive = permStatus.receive
    if (receive === 'prompt' || receive === 'prompt-with-rationale') {
      const requested = await PushNotifications.requestPermissions()
      receive = requested.receive
    }
    if (receive !== 'granted') return // user said no — respect it silently, no nagging

    await PushNotifications.register()
  } catch (err) {
    // Push is a nicety, not a requirement for using the app — never let a
    // permission/registration hiccup surface to the user or block sign-in.
    console.error('[pushNotifications] setup failed:', err)
  }
}

/** Deletes this device's token server-side. Call on logout. */
export async function teardownPushNotifications() {
  if (!IS_NATIVE_APP || !lastRegisteredToken) return
  try {
    await devicesApi.unregister(lastRegisteredToken)
  } catch {
    // Best-effort — a failed unregister just means this device keeps
    // getting pushes for an account it's no longer signed into, which the
    // next login's re-registration (upsert by token) corrects anyway.
  } finally {
    lastRegisteredToken = null
  }
}

function attachListenersOnce() {
  if (listenersAttached) return
  listenersAttached = true

  PushNotifications.addListener('registration', async (token) => {
    lastRegisteredToken = token.value
    try {
      await devicesApi.register(token.value, import.meta.env.VITE_SENTRY_RELEASE)
    } catch (err) {
      console.error('[pushNotifications] failed to save device token:', err)
    }
  })

  PushNotifications.addListener('registrationError', (err) => {
    console.error('[pushNotifications] registration error:', err)
  })

  // Foreground delivery — iOS doesn't show a banner for a push that arrives
  // while the app is open, so this is the only signal the user gets. Kept
  // deliberately simple (no UI library dependency): a lightweight
  // top-of-screen banner would be a nicer follow-up, but a console log plus
  // whatever badge/sound the OS already applies is enough for launch.
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('[pushNotifications] received in foreground:', notification.title, notification.body)
  })

  // Tapped from the notification centre / lock screen — the payload's
  // `data.leagueId` (set by draftReminderService.js) tells us where to go.
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const leagueId = action.notification?.data?.leagueId
    if (leagueId) {
      window.location.href = `/league/${leagueId}`
    }
  })
}
